/** Seed data for the Catalogue Governance demo screen (Demo Mode only - no real backend).
 * Mock data files are only ever imported by the mocked API client, never by page components. */

import {
  AvailabilitySubstitutionRow,
  BlockedItemRow,
  DataQualityIssueRow,
  DuplicateProductRow,
  GovernanceIssueRow,
  OffContractRequestRow,
  PriceChangeRow,
} from '../../app/services/api/catalogueGovernance/catalogueGovernanceTypes';

export const catalogueGovernanceIssuesSeed: GovernanceIssueRow[] = [
  {
    id: 'GI-001', issue: 'Supplier price increase pending approval', supplier: 'FreshCo Wholesale', catalogue: 'Core Grocery',
    product: 'Unsalted Butter Block 5kg', severity: 'High', affectedSites: 'All sites', owner: 'Commercial Team',
    status: 'Pending', dueDate: '2026-07-15',
  },
  {
    id: 'GI-002', issue: 'Ingredient recall - item blocked', supplier: 'Mediterranean Oils', catalogue: 'Oils & Condiments',
    product: 'Extra Virgin Olive Oil 5L', severity: 'Critical', affectedSites: 'All sites', owner: 'Compliance Team',
    status: 'Open', dueDate: '2026-07-10',
  },
  {
    id: 'GI-003', issue: 'Missing allergen data', supplier: 'Bakewell Supplies', catalogue: 'Bakery',
    product: 'Wholemeal Flour 16kg', severity: 'High', affectedSites: '0101, 0145', owner: 'Compliance Team',
    status: 'Open', dueDate: '2026-07-12',
  },
  {
    id: 'GI-004', issue: 'Off-contract spend flagged', supplier: 'Local Farm Direct', catalogue: 'Produce',
    product: 'Purple Sprouting Broccoli', severity: 'Medium', affectedSites: '0145', owner: 'Procurement Team',
    status: 'Pending', dueDate: '2026-07-20',
  },
  {
    id: 'GI-005', issue: 'Possible duplicate product', supplier: 'Brakes', catalogue: 'Core Grocery',
    product: 'Chopped Tomatoes (Tinned)', severity: 'Low', affectedSites: 'All sites', owner: 'Catalogue Team',
    status: 'Open', dueDate: '2026-07-25',
  },
  {
    id: 'GI-006', issue: 'Product unavailable - substitute awaiting approval', supplier: 'Dairy Farmers Ltd', catalogue: 'Dairy',
    product: 'Cheddar Mild 2.5kg', severity: 'Medium', affectedSites: 'All sites', owner: 'Procurement Team',
    status: 'Pending', dueDate: '2026-07-14',
  },
  {
    id: 'GI-007', issue: 'UOM conversion error', supplier: 'Bulk Oils Ltd', catalogue: 'Oils & Condiments',
    product: 'Sunflower Oil 20L', severity: 'Medium', affectedSites: 'All sites', owner: 'Catalogue Team',
    status: 'Open', dueDate: '2026-07-18',
  },
];

export const priceChangesSeed: PriceChangeRow[] = [
  {
    id: 'PC-001', supplier: 'FreshCo Wholesale', catalogue: 'Core Grocery', product: 'Unsalted Butter Block 5kg',
    currentPrice: 24.5, newPrice: 26.8, effectiveDate: '2026-08-01', approvalStatus: 'Pending',
    affectedRecipes: 12, affectedMenus: 4, affectedSites: 38, estimatedCostImpact: 3120.5,
  },
  {
    id: 'PC-002', supplier: 'Mediterranean Oils', catalogue: 'Oils & Condiments', product: 'Extra Virgin Olive Oil 5L',
    currentPrice: 18.2, newPrice: 17.4, effectiveDate: '2026-07-22', approvalStatus: 'Approved',
    affectedRecipes: 20, affectedMenus: 6, affectedSites: 40, estimatedCostImpact: -890.0,
  },
  {
    id: 'PC-003', supplier: 'Bakewell Supplies', catalogue: 'Bakery', product: 'Wholemeal Flour 16kg',
    currentPrice: 9.6, newPrice: 11.9, effectiveDate: '2026-08-05', approvalStatus: 'Pending',
    affectedRecipes: 8, affectedMenus: 3, affectedSites: 32, estimatedCostImpact: 2145.0,
  },
  {
    id: 'PC-004', supplier: 'Dairy Farmers Ltd', catalogue: 'Dairy', product: 'Cheddar Mild 2.5kg',
    currentPrice: 12.3, newPrice: 15.9, effectiveDate: '2026-07-18', approvalStatus: 'Rejected',
    affectedRecipes: 15, affectedMenus: 5, affectedSites: 40, estimatedCostImpact: 4210.0,
  },
  {
    id: 'PC-005', supplier: 'Brakes', catalogue: 'Core Grocery', product: 'Chopped Tomatoes (Tinned) x12',
    currentPrice: 7.1, newPrice: 7.35, effectiveDate: '2026-08-10', approvalStatus: 'Pending',
    affectedRecipes: 10, affectedMenus: 4, affectedSites: 40, estimatedCostImpact: 315.0,
  },
];

export const availabilitySubstitutionsSeed: AvailabilitySubstitutionRow[] = [
  {
    id: 'AV-001', supplier: 'Dairy Farmers Ltd', product: 'Cheddar Mild 2.5kg', catalogue: 'Dairy',
    availabilityStatus: 'Unavailable', unavailableFrom: '2026-07-01', expectedReturnDate: '2026-08-15',
    affectedSites: 'All sites', approvedSubstitute: 'Cheddar Mild 2.5kg (Alt. Brand)', substitutionType: 'AlternativeBrand',
    approvalStatus: 'Pending', allergenImpact: 'No change - milk allergen retained', costImpact: 0.85,
  },
  {
    id: 'AV-002', supplier: 'Saffron & Spice Co', product: 'Saffron Threads 50g', catalogue: 'Herbs & Spices',
    availabilityStatus: 'Discontinued', unavailableFrom: '2026-06-15', expectedReturnDate: null,
    affectedSites: '0101, 0102', approvedSubstitute: 'Saffron Threads 50g (Premium)', substitutionType: 'LikeForLike',
    approvalStatus: 'Approved', allergenImpact: 'No allergen impact', costImpact: 4.2,
  },
  {
    id: 'AV-003', supplier: 'Local Farm Direct', product: 'Purple Sprouting Broccoli', catalogue: 'Produce',
    availabilityStatus: 'LimitedStock', unavailableFrom: '2026-07-05', expectedReturnDate: '2026-07-19',
    affectedSites: '0145', approvedSubstitute: null, substitutionType: null,
    approvalStatus: 'Pending', allergenImpact: 'Not assessed', costImpact: 0,
  },
  {
    id: 'AV-004', supplier: 'Bulk Oils Ltd', product: 'Sunflower Oil 20L', catalogue: 'Oils & Condiments',
    availabilityStatus: 'Unavailable', unavailableFrom: '2026-06-28', expectedReturnDate: '2026-07-30',
    affectedSites: 'All sites', approvedSubstitute: 'Rapeseed Oil 20L', substitutionType: 'RecipeChangeRequired',
    approvalStatus: 'Rejected', allergenImpact: 'No allergen impact - flavour profile differs', costImpact: -1.1,
  },
];

export const offContractRequestsSeed: OffContractRequestRow[] = [
  {
    id: 'OC-001', site: '0145 - City Centre', requestedItem: 'Passion Fruit (Case)', supplier: 'Speciality Fruit Importers',
    reason: 'Menu special not covered by core catalogue', urgency: 'Medium', estimatedValue: 220.0,
    existingApprovedAlternative: null, requestedBy: 'J. Ashworth (Head Chef)', approvalStatus: 'Pending', submittedDate: '2026-07-02',
  },
  {
    id: 'OC-002', site: '0102 - Riverside', requestedItem: 'Macadamia Nuts (Whole) 5kg', supplier: 'Nutworks Direct',
    reason: 'Approved supplier out of stock', urgency: 'High', estimatedValue: 160.0,
    existingApprovedAlternative: 'Macadamia Nuts (Whole) 5kg - Brakes', requestedBy: 'M. Okafor (Sous Chef)',
    approvalStatus: 'Pending', submittedDate: '2026-07-04',
  },
  {
    id: 'OC-003', site: '0101 - High Street', requestedItem: 'Artisan Sourdough Starter', supplier: 'Heritage Bakehouse',
    reason: 'New menu development trial', urgency: 'Low', estimatedValue: 45.0,
    existingApprovedAlternative: null, requestedBy: 'R. Singh (Development Chef)', approvalStatus: 'ApprovedOnce', submittedDate: '2026-06-28',
  },
  {
    id: 'OC-004', site: '0210 - Retail Park', requestedItem: 'Truffle Oil 250ml', supplier: 'Gourmet Imports Ltd',
    reason: 'Local supplier relationship, better price', urgency: 'Low', estimatedValue: 38.5,
    existingApprovedAlternative: 'Truffle Oil 250ml - Mediterranean Oils', requestedBy: 'K. Lewis (Store Manager)',
    approvalStatus: 'Rejected', submittedDate: '2026-06-20',
  },
];

export const dataQualityIssuesSeed: DataQualityIssueRow[] = [
  {
    id: 'DQ-001', issueType: 'MissingAllergens', supplier: 'Bakewell Supplies', catalogue: 'Bakery',
    product: 'Wholemeal Flour 16kg', severity: 'High', description: 'No allergen data submitted by supplier.',
    suggestedFix: 'Request allergen declaration from supplier portal.', downstreamImpact: '8 recipes cannot confirm allergen labelling.',
    status: 'Open',
  },
  {
    id: 'DQ-002', issueType: 'BadUomConversion', supplier: 'Bulk Oils Ltd', catalogue: 'Oils & Condiments',
    product: 'Sunflower Oil 20L', severity: 'Medium', description: 'Case-to-each conversion factor inconsistent with pack size.',
    suggestedFix: 'Update conversion factor to 20 (litres per case).', downstreamImpact: 'Recipe costings understated by ~12%.',
    status: 'Open',
  },
  {
    id: 'DQ-003', issueType: 'UnexpectedPriceMovement', supplier: 'Dairy Farmers Ltd', catalogue: 'Dairy',
    product: 'Cheddar Mild 2.5kg', severity: 'High', description: 'Price increased 29% since last catalogue upload.',
    suggestedFix: 'Confirm with supplier before applying automatically.', downstreamImpact: 'Recipe cost and menu margin reports.',
    status: 'Open',
  },
  {
    id: 'DQ-004', issueType: 'InactiveItemUsedInRecipe', supplier: 'Local Farm Direct', catalogue: 'Produce',
    product: 'Purple Sprouting Broccoli', severity: 'Medium', description: 'Item marked inactive but referenced by 3 active recipes.',
    suggestedFix: 'Replace with active substitute or reactivate item.', downstreamImpact: 'Recipes may fail costing/ordering.',
    status: 'Open',
  },
  {
    id: 'DQ-005', issueType: 'MissingNutrition', supplier: 'Heritage Bakehouse', catalogue: 'Bakery',
    product: 'Artisan Sourdough Starter', severity: 'Low', description: 'Nutrition panel not provided.',
    suggestedFix: 'Request nutrition data at next catalogue refresh.', downstreamImpact: 'Menu nutrition labelling incomplete.',
    status: 'Ignored',
  },
  {
    id: 'DQ-006', issueType: 'InconsistentPackSize', supplier: 'Brakes', catalogue: 'Core Grocery',
    product: 'Chopped Tomatoes (Tinned) x12', severity: 'Low', description: 'Pack size differs from previous catalogue upload (x12 vs x6).',
    suggestedFix: 'Confirm correct pack size with supplier.', downstreamImpact: 'Order quantities may be miscalculated.',
    status: 'Resolved',
  },
  {
    id: 'DQ-007', issueType: 'MissingCarbon', supplier: 'Mediterranean Oils', catalogue: 'Oils & Condiments',
    product: 'Extra Virgin Olive Oil 5L', severity: 'Low', description: 'Carbon footprint data not supplied.',
    suggestedFix: 'Request carbon data as part of sustainability review.', downstreamImpact: 'Sustainability reporting incomplete.',
    status: 'Open',
  },
  {
    id: 'DQ-008', issueType: 'MissingPrice', supplier: 'Speciality Fruit Importers', catalogue: 'Produce',
    product: 'Passion Fruit (Case)', severity: 'Medium', description: 'No price submitted for current catalogue period.',
    suggestedFix: 'Escalate to supplier account manager.', downstreamImpact: 'Item cannot be ordered through core catalogue.',
    status: 'Open',
  },
];

export const duplicateProductsSeed: DuplicateProductRow[] = [
  {
    id: 'DP-001', primaryProduct: 'Chopped Tomatoes (Tinned) x12', possibleDuplicate: 'Chopped Tomatoes (Canned) x12',
    supplier: 'Brakes', catalogue: 'Core Grocery', matchConfidence: 0.92, reason: 'Near-identical description, same pack size.',
    recipeUsage: 14, menuUsage: 5, suggestedAction: 'Merge', resolutionNote: null,
  },
  {
    id: 'DP-002', primaryProduct: 'Extra Virgin Olive Oil 5L', possibleDuplicate: 'EVOO Premium 5L',
    supplier: 'Mediterranean Oils', catalogue: 'Oils & Condiments', matchConfidence: 0.85, reason: 'Matching supplier SKU prefix.',
    recipeUsage: 20, menuUsage: 6, suggestedAction: 'KeepBoth', resolutionNote: null,
  },
  {
    id: 'DP-003', primaryProduct: 'Unsalted Butter Block 5kg', possibleDuplicate: 'Butter Unsalted Block 5kg',
    supplier: 'FreshCo Wholesale', catalogue: 'Core Grocery', matchConfidence: 0.98, reason: 'Matching GTIN.',
    recipeUsage: 12, menuUsage: 4, suggestedAction: 'Merge', resolutionNote: null,
  },
  {
    id: 'DP-004', primaryProduct: 'Wholemeal Flour 16kg', possibleDuplicate: 'Wholemeal Flour (Discontinued) 16kg',
    supplier: 'Bakewell Supplies', catalogue: 'Bakery', matchConfidence: 0.79, reason: 'Description match, discontinued variant still active.',
    recipeUsage: 8, menuUsage: 3, suggestedAction: 'ReplaceInRecipes', resolutionNote: null,
  },
  {
    id: 'DP-005', primaryProduct: 'Cheddar Mild 2.5kg', possibleDuplicate: 'Cheddar Mild (Delisted) 2.5kg',
    supplier: 'Dairy Farmers Ltd', catalogue: 'Dairy', matchConfidence: 0.65, reason: 'Similar description, different supplier SKU.',
    recipeUsage: 15, menuUsage: 5, suggestedAction: 'BlockDuplicate', resolutionNote: null,
  },
];

export const blockedItemsSeed: BlockedItemRow[] = [
  {
    id: 'BL-001', product: 'Extra Virgin Olive Oil 5L', supplier: 'Mediterranean Oils', catalogue: 'Oils & Condiments',
    reason: 'Ingredient recall in effect - blocked from ordering pending supplier confirmation.',
    blockedSince: '2026-07-01', blockedBy: 'Compliance Team', affectedSites: 'All sites', status: 'Blocked',
  },
  {
    id: 'BL-002', product: 'Saffron Threads 50g', supplier: 'Saffron & Spice Co', catalogue: 'Herbs & Spices',
    reason: 'Supplier compliance certification expired.',
    blockedSince: '2026-06-20', blockedBy: 'Procurement Team', affectedSites: 'All sites', status: 'Blocked',
  },
  {
    id: 'BL-003', product: 'Truffle Oil 250ml', supplier: 'Gourmet Imports Ltd', catalogue: 'Oils & Condiments',
    reason: 'Unapproved supplier - awaiting onboarding review.',
    blockedSince: '2026-06-15', blockedBy: 'Procurement Team', affectedSites: '0210', status: 'Blocked',
  },
];
